import * as React from "react";
import { format } from "date-fns";
import { ChevronDown, ChevronLeft, ChevronRight } from "lucide-react";
import { DayPicker, type DropdownProps } from "react-day-picker";

import { cn } from "@/lib/utils";
import { buttonVariants } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

export type CalendarProps = React.ComponentProps<typeof DayPicker>;

// Render react-day-picker's month/year dropdowns using the themed shadcn Select
// instead of the native <select>, so the options popup matches our design.
function CalendarDropdown({
  value,
  onChange,
  options = [],
  "aria-label": ariaLabel,
}: DropdownProps) {
  const selected = options.find((o) => String(o.value) === String(value));
  const handleChange = (nextValue: string) => {
    const event = {
      target: { value: nextValue },
    } as unknown as React.ChangeEvent<HTMLSelectElement>;
    onChange?.(event);
  };

  return (
    <Select value={String(value)} onValueChange={handleChange}>
      <SelectTrigger
        size="sm"
        aria-label={ariaLabel}
        className="h-8 w-fit gap-1 rounded-md border-border/70 bg-muted/50 px-2.5 text-sm font-medium hover:bg-muted [&>svg]:size-3.5 [&>svg]:text-muted-foreground"
      >
        <SelectValue>{selected?.label}</SelectValue>
      </SelectTrigger>
      <SelectContent className="z-[110] max-h-72">
        {options.map((opt) => (
          <SelectItem
            key={opt.value}
            value={String(opt.value)}
            disabled={opt.disabled}
          >
            {opt.label}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

function Calendar({
  className,
  classNames,
  showOutsideDays = true,
  captionLayout,
  ...props
}: CalendarProps) {
  return (
    <DayPicker
      showOutsideDays={showOutsideDays}
      captionLayout={captionLayout}
      className={cn("p-3", className)}
      classNames={{
        months:
          "flex flex-col sm:flex-row space-y-4 sm:space-x-4 sm:space-y-0 relative",
        month: "space-y-4",
        month_caption: "flex justify-center relative items-center h-9",
        caption_label:
          "z-caption font-medium text-foreground inline-flex items-center rounded-full border border-border/70 bg-muted/50 px-3 py-1",
        dropdowns: "flex items-center justify-center gap-2",
        nav: "pointer-events-none absolute top-0 inset-x-0 z-10 flex items-center justify-between w-full px-0 h-9",
        button_previous: cn(
          buttonVariants({ variant: "outline" }),
          "pointer-events-auto h-7 w-7 bg-transparent p-0 opacity-50 hover:opacity-100"
        ),
        button_next: cn(
          buttonVariants({ variant: "outline" }),
          "pointer-events-auto h-7 w-7 bg-transparent p-0 opacity-50 hover:opacity-100"
        ),
        month_grid: "w-full border-collapse",
        weekdays: "flex w-full",
        weekday:
          "text-muted-foreground w-9 z-secondary flex justify-center items-center",
        week: "flex w-full mt-2",
        day: cn(
          "h-9 w-9 text-center p-0 relative flex justify-center items-center",
          "focus-within:relative focus-within:z-20"
        ),
        day_button: cn(
          buttonVariants({ variant: "ghost" }),
          "h-9 w-9 p-0 text-sm tabular-nums"
        ),
        range_start:
          "day-range-start bg-accent rounded-l-full [&_button]:bg-primary [&_button]:text-primary-foreground [&_button]:hover:bg-primary/90",
        range_end:
          "day-range-end bg-accent rounded-r-full [&_button]:bg-primary [&_button]:text-primary-foreground [&_button]:hover:bg-primary/90",
        range_middle:
          "bg-accent [&_button]:!bg-transparent [&_button]:!text-accent-foreground [&_button]:!shadow-none [&_button]:!rounded-none [&_button]:hover:!bg-accent-foreground/10",
        selected:
          "[&_button]:bg-primary [&_button]:text-primary-foreground [&_button]:hover:bg-primary/90 [&_button]:hover:text-primary-foreground",
        today:
          "[&_button]:font-semibold [&_button]:ring-1 [&_button]:ring-border [&_button]:rounded-full",
        outside:
          "day-outside text-muted-foreground opacity-50 aria-selected:bg-accent/50 aria-selected:text-muted-foreground aria-selected:opacity-30",
        disabled: "text-muted-foreground opacity-50",
        hidden: "invisible",
        ...classNames,
      }}
      components={{
        Chevron: ({ orientation }) => {
          if (orientation === "left")
            return <ChevronLeft className="h-4 w-4" />;
          if (orientation === "right")
            return <ChevronRight className="h-4 w-4" />;
          return <ChevronDown className="h-4 w-4" />;
        },
        Dropdown: CalendarDropdown,
      }}
      formatters={{
        formatCaption: (date) => format(date, "MMM yyyy"),
      }}
      {...props}
    />
  );
}
Calendar.displayName = "Calendar";

export { Calendar };
